import { useState, useCallback } from 'react'
import { Eye, EyeOff, CheckCircle2, Loader2 } from 'lucide-react'
import type { EventoSettings } from '@/services/api/eventoSync'

interface EventoKeysSectionProps {
  settings: EventoSettings
  onUpdate: (payload: Partial<EventoSettings>) => Promise<void>
}

export function EventoKeysSection({ settings, onUpdate }: EventoKeysSectionProps) {
  const [form, setForm] = useState<Partial<EventoSettings>>({})
  const [showQwenKey, setShowQwenKey] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const val = <T extends keyof EventoSettings>(key: T): EventoSettings[T] => {
    return form[key] !== undefined ? form[key] as EventoSettings[T] : settings[key]
  }

  const update = (key: keyof EventoSettings, value: unknown) => {
    setForm(f => ({ ...f, [key]: value }))
  }

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== undefined)
      )
      await onUpdate(payload)
      setForm({})
      setSuccess('Einstellungen gespeichert')
    } catch (err) {
      setError('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }, [form, onUpdate])

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500">&times;</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <CheckCircle2 className="w-4 h-4" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500">&times;</button>
        </div>
      )}

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Qwen API (PDF-Parsing)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Optional: Überschreibt den systemweiten Qwen API-Key für diese Schule.
          Lasse das Feld leer um den System-Standard zu verwenden.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Qwen API Key</label>
          <div className="relative">
            <input type={showQwenKey ? 'text' : 'password'} value={val('qwen_api_key') ?? ''} onChange={e => update('qwen_api_key', e.target.value || null)} placeholder="••••••••" className="w-full px-3 py-2 border rounded-lg pr-10 text-sm" />
            <button type="button" onClick={() => setShowQwenKey(!showQwenKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showQwenKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Speichern
        </button>
      </div>
    </div>
  )
}